/**
 * „Offen <Zahl>“ — Fragen der Entwickler an Martin, die noch keine Antwort haben. (20.10.2026, erste Fassung)
 *
 * Martin, 20.10. 09:39 (gesprochen): „… dass Fragen, die noch nicht beantwortet sind, irgendwo markiert sind, sodass ich draufklicken
 * kann, wie eine Art Pipeline, damit keine Frage vergessen wird.“ Anlass: der Zimmermann fragte, Martin sprach gleich danach den Hüter
 * an — für die ältere Frage ging kein Mikro auf (`[ptt] nach-frage übersprungen neuer-zug`), und sie blieb unbeantwortet, ohne dass er
 * es sah. Das Mikro bleibt, wie es ist; diese Liste vergisst die Frage nicht.
 *
 * NUR ANZEIGE im Browser: kein Serverweg, kein Ja, keine Freigabe. „Antworten“ an einem Eintrag führt zur Blase, stellt „An:“ auf den
 * Absender und öffnet das Antwortfeld — das Mikro öffnet NICHT, Martin drückt selbst.
 *
 * Erfassung (app.ts, Live-Beitrag): ein frischer Beitrag von Schmied, Zimmermann oder Hüter, den `schmiedFragtMartin`
 * (lib/schmied-warten.ts) als Frage an Martin erkennt (Ja/Nein-Karte, Server-Feld `erwartet`, Text endet mit Frage).
 *
 * Erledigt-Regel (EIN Besitzer, hier):
 *   - Martin schickt danach eine Nachricht an GENAU diesen Absender (der Server bestätigt den Empfänger; eine Karten-Antwort geht an den
 *     Schmied) → alle offenen Fragen dieses Absenders bis zu diesem Zeitpunkt sind erledigt;
 *   - Martin tippt „Erledigt“ (weg) oder „Später“ (eine Stunde ausgeblendet, dann wieder da);
 *   - derselbe Absender meldet danach „arbeitet weiter“ oder eine reine Info (`absenderMachteWeiter`, 22.10.2026) → seine älteren
 *     Fragen erledigen sich selbst (er wartet nicht mehr);
 *   - nach OFFEN_VERFALL_MS (24 h) verfällt sie von selbst.
 *   NIE erledigt durch Aussagen anderer Absender oder eine Nachricht an jemand anderen.
 *
 * Gespeichert wird nur Absender, Zeit und Kurztext (localStorage, jeder Zugriff in try/catch — ohne Speicher läuft alles im Speicher).
 * Journalzeilen tragen nie Text, nur Absender und Zahl.
 */
import { entwickler, istEntwicklerId, type EntwicklerId } from "../../shared/entwickler.js";
import { TAFEL_AUF, meldeTafelAuf } from "./schmied-arbeit.js";
import { ohneCode, schmiedFragtMartin } from "./schmied-warten.js";

export const OFFEN_SCHLUESSEL = "voice-v8.offene-fragen";
export const OFFEN_VERFALL_MS = 24 * 60 * 60 * 1000;
export const OFFEN_SPAETER_MS = 60 * 60 * 1000;
export const OFFEN_HOECHSTENS = 20;
export const OFFEN_KURZ_ZEICHEN = 80;

export interface OffeneFrage {
  /** `<absender>-<ts>` — dieselbe Frage zweimal gemeldet zählt einmal. */
  id: string;
  von: EntwicklerId;
  /** Zeit der Blase (ihr `data-ts`) — der Tipp findet sie darüber wieder. */
  ts: number;
  kurz: string;
  /** Eine Ja/Nein-Karte: erledigt sich NICHT durch „weiter“/„info“ des Absenders (nur Antwort, Tipp oder Verfall). */
  ja?: true;
  /** „Später“: bis dahin ausgeblendet. */
  spaeterBis?: number;
}

type Speicher = Pick<Storage, "getItem" | "setItem">;

export interface OffeneFragenOptionen {
  jetzt?: () => number;
  /** `null`: nichts speichern (Tests, Privatmodus). Ohne Angabe: localStorage, falls erreichbar. */
  speicher?: Speicher | null;
  /** Eine Journalzeile — ohne Textinhalt. */
  melde?: (zeile: string) => void;
  /** Der Stand hat sich geändert (die Anzeige zeichnet neu). */
  geaendert?: () => void;
  /**
   * Das angemeldete Konto (`__voiceV8Uid`). Nur Konto 1 hat offene Fragen (wie die Marke „Entwicklung“) — jedes andere Konto, auch
   * „noch unbekannt“, sieht eine leere Liste und es wird nichts gespeichert. Gespeichert wird je Konto (`<Schlüssel>.<Konto>`).
   * Ohne Angabe (Tests): Konto 1.
   */
  konto?: () => unknown;
}

/** Nur dieses Konto führt die Liste (Martin; die Entwickler gibt es nur dort). */
export const OFFEN_KONTO = 1;

/** Der Speicherschlüssel je Konto. Rein. */
export function offenSchluessel(konto: number): string {
  return `${OFFEN_SCHLUESSEL}.${konto}`;
}

/** Die ersten ~80 Zeichen, ohne Code und Zeilenumbrüche. Rein. */
export function kurzText(text: string): string {
  const flach = ohneCode(text ?? "").replace(/\s+/g, " ").trim();
  if (flach.length <= OFFEN_KURZ_ZEICHEN) return flach;
  return `${flach.slice(0, OFFEN_KURZ_ZEICHEN - 1).trimEnd()}…`;
}

/**
 * Wer fragt Martin mit diesem Live-Beitrag? Der Absender (Schmied, Zimmermann, Hüter) — oder `null`: kein Entwickler, unbekannter
 * Absender, alter (nicht frischer) Beitrag, oder keine Frage an Martin (`schmiedFragtMartin`: Ja/Nein-Karte, `erwartet`, Frage am Ende;
 * nie bei `weiter`/`info`, nie an Vespera). `an` zählt nur beim Schmied (wie in app.ts); bei Zimmermann/Hüter zählt an seiner Stelle das
 * Server-Feld `ziel` (22.10.2026: Frage an einen anderen Entwickler, nur vom Bus, nie aus dem Text). Rein.
 */
export function frageVon(b: {
  entwicklerId: unknown;
  frisch: boolean;
  text: string;
  frage: unknown;
  weiter: unknown;
  info: unknown;
  an: unknown;
  erwartet: unknown;
  ziel?: unknown;
}): EntwicklerId | null {
  if (!b.frisch || !istEntwicklerId(b.entwicklerId)) return null;
  const schmied = b.entwicklerId === "schmied";
  return schmiedFragtMartin({
    text: b.text,
    frage: schmied ? b.frage : null,
    weiter: b.weiter,
    info: b.info,
    an: schmied ? b.an : b.ziel,
    erwartet: b.erwartet,
  })
    ? b.entwicklerId
    : null;
}

function gueltig(x: unknown): OffeneFrage | null {
  const e = x as Partial<OffeneFrage> | null;
  if (!e || typeof e !== "object") return null;
  if (!istEntwicklerId(e.von) || typeof e.ts !== "number" || !Number.isFinite(e.ts)) return null;
  if (typeof e.kurz !== "string" || e.kurz.length > OFFEN_KURZ_ZEICHEN * 2) return null;
  const f: OffeneFrage = { id: `${e.von}-${e.ts}`, von: e.von, ts: e.ts, kurz: e.kurz };
  if (typeof e.spaeterBis === "number" && Number.isFinite(e.spaeterBis)) f.spaeterBis = e.spaeterBis;
  if (e.ja === true) f.ja = true;
  return f;
}

function standardSpeicher(): Speicher | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export class OffeneFragen {
  private fragen: OffeneFrage[] = [];
  private readonly jetzt: () => number;
  private readonly speicher: Speicher | null;
  private readonly melde: (zeile: string) => void;
  private readonly geaendert: () => void;
  private readonly konto: () => unknown;
  /** Für welches Konto `fragen` gerade geladen ist (`null`: keines — leere Liste). */
  private geladenFuer: number | null = null;

  constructor(o: OffeneFragenOptionen = {}) {
    this.jetzt = o.jetzt ?? (() => Date.now());
    this.speicher = o.speicher === undefined ? standardSpeicher() : o.speicher;
    this.melde = o.melde ?? (() => undefined);
    this.geaendert = o.geaendert ?? (() => undefined);
    this.konto = o.konto ?? (() => OFFEN_KONTO);
    this.passeKonto();
  }

  /** Gilt die Liste für das angemeldete Konto? Wechselt das Konto, wird die Liste des neuen geladen (andere: leer). */
  private passeKonto(): boolean {
    let k: unknown;
    try {
      k = this.konto();
    } catch {
      k = null;
    }
    const fuer = k === OFFEN_KONTO ? OFFEN_KONTO : null;
    if (fuer !== this.geladenFuer) {
      this.fragen = [];
      this.geladenFuer = fuer;
      if (fuer !== null) this.lade(fuer);
    }
    return fuer !== null;
  }

  /** Ein Beitrag dieses Absenders fragt Martin. Ungültiger Absender oder anderes Konto: nichts. */
  frageKam(von: unknown, text: string, ts: number, ja = false): void {
    if (!this.passeKonto() || !istEntwicklerId(von) || !Number.isFinite(ts)) return;
    const id = `${von}-${ts}`;
    if (this.fragen.some((f) => f.id === id)) return;
    this.fragen.push(ja ? { id, von, ts, kurz: kurzText(text), ja: true } : { id, von, ts, kurz: kurzText(text) });
    this.fragen.sort((a, b) => a.ts - b.ts);
    if (this.fragen.length > OFFEN_HOECHSTENS) this.fragen.splice(0, this.fragen.length - OFFEN_HOECHSTENS);
    this.melde(`[offen] frage von=${von} n=${this.liste().length}`);
    this.sichere();
  }

  /**
   * Martins Nachricht ging (vom Server bestätigt) an `an`. Erledigt NUR Fragen genau dieses Absenders, die vor `bis` (ohne Angabe: jetzt)
   * kamen — eine Nachricht an jemand anderen oder eine ältere, spät eintreffende Nachricht erledigt nichts. Gibt die Zahl zurück.
   */
  martinAntwortete(an: unknown, bis: number = this.jetzt()): number {
    if (!this.passeKonto() || !istEntwicklerId(an)) return 0;
    const vorher = this.fragen.length;
    this.fragen = this.fragen.filter((f) => !(f.von === an && f.ts <= bis));
    const weg = vorher - this.fragen.length;
    if (weg > 0) {
      this.melde(`[offen] erledigt von=${an} weil=antwort n=${weg}`);
      this.sichere();
    }
    return weg;
  }

  /**
   * Derselbe Absender meldet danach „arbeitet weiter“ (`weiter`) oder eine reine Info (`info`) — er wartet also nicht mehr auf
   * Martin: seine offenen Fragen bis `bis` erledigen sich selbst (22.10.2026, Martin: Einträge, die keine offene Frage mehr sind,
   * standen weiter in der Liste). Nur genau dieser Absender, nie ein anderer; Ja/Nein-Karten (`ja`) bleiben stehen. Gibt die Zahl zurück.
   */
  absenderMachteWeiter(von: unknown, bis: number = this.jetzt()): number {
    if (!this.passeKonto() || !istEntwicklerId(von)) return 0;
    const vorher = this.fragen.length;
    this.fragen = this.fragen.filter((f) => !(f.von === von && f.ts < bis && !f.ja));
    const weg = vorher - this.fragen.length;
    if (weg > 0) {
      this.melde(`[offen] erledigt von=${von} weil=weiter n=${weg}`);
      this.sichere();
    }
    return weg;
  }

  /** „Erledigt“ getippt: weg. */
  erledigt(id: string): void {
    if (!this.passeKonto()) return;
    const f = this.fragen.find((x) => x.id === id);
    if (!f) return;
    this.fragen = this.fragen.filter((x) => x.id !== id);
    this.melde(`[offen] erledigt von=${f.von} weil=weggeklickt`);
    this.sichere();
  }

  /** „Später“ getippt: eine Stunde ausgeblendet (der Verfall nach 24 h gilt weiter). */
  spaeter(id: string): void {
    if (!this.passeKonto()) return;
    const f = this.fragen.find((x) => x.id === id);
    if (!f) return;
    f.spaeterBis = this.jetzt() + OFFEN_SPAETER_MS;
    this.melde(`[offen] später von=${f.von}`);
    this.sichere();
  }

  /** Die offenen, gerade sichtbaren Fragen, älteste zuerst. Verfallene fallen dabei heraus. */
  liste(): OffeneFrage[] {
    if (!this.passeKonto()) return [];
    const t = this.jetzt();
    const frisch = this.fragen.filter((f) => t - f.ts < OFFEN_VERFALL_MS);
    if (frisch.length !== this.fragen.length) {
      this.melde(`[offen] verfallen n=${this.fragen.length - frisch.length}`);
      this.fragen = frisch;
      this.speichere();
    }
    return this.fragen.filter((f) => !(f.spaeterBis !== undefined && f.spaeterBis > t)).map((f) => ({ ...f }));
  }

  private lade(konto: number): void {
    try {
      const roh = this.speicher?.getItem(offenSchluessel(konto));
      if (!roh) return;
      const daten: unknown = JSON.parse(roh);
      if (!Array.isArray(daten)) return;
      const gesehen = new Set<string>();
      for (const x of daten.slice(-OFFEN_HOECHSTENS)) {
        const f = gueltig(x);
        if (!f || gesehen.has(f.id)) continue;
        gesehen.add(f.id);
        this.fragen.push(f);
      }
      this.fragen.sort((a, b) => a.ts - b.ts);
    } catch {
      this.fragen = [];
    }
  }

  private speichere(): void {
    if (this.geladenFuer === null) return;
    try {
      this.speicher?.setItem(offenSchluessel(this.geladenFuer), JSON.stringify(this.fragen.map(({ von, ts, kurz, ja, spaeterBis }) => ({ von, ts, kurz, ...(ja ? { ja } : {}), ...(spaeterBis ? { spaeterBis } : {}) }))));
    } catch {
      /* voll oder gesperrt — der Stand gilt bis zum Neuladen */
    }
  }

  private sichere(): void {
    this.speichere();
    try {
      this.geaendert();
    } catch {
      /* die Anzeige darf den Stand nie umwerfen */
    }
  }
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Anzeige: Marke „Offen <Zahl>“ in der Kopfzeile (neben „Entwicklung“) und ihre Tafel. Jeder Text geht nur über textContent hinein.
// ---------------------------------------------------------------------------------------------------------------------------------

const MARKE_ID = "topbar-offen";
const ZAHL_ID = "topbar-offen-zahl";
const LISTE_ID = "topbar-offen-liste";
const AUFFRISCHEN_MS = 60_000;

/** Der Name eines Absenders, wie ihn die App zeigt. */
export function absenderName(von: EntwicklerId): string {
  return von === "schmied" ? "Schmied" : entwickler(von).name;
}

/** „09:39“ — Uhrzeit der Frage nach der Uhr dieser Seite. */
export function uhrzeit(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Der Satz für Screenreader an der Marke — beginnt mit dem sichtbaren „Offen <Zahl>“ (Sprachsteuerung: „Tippe Offen“). */
export function offenSatz(n: number): string {
  return `Offen ${n}: ${n === 1 ? "eine Frage wartet" : `${n} Fragen warten`} auf deine Antwort`;
}

/** Wohin der Fokus geht, wenn die letzte Frage weg ist und die Marke verschwindet: der Menü-Knopf (steht immer im Kopf). */
export const FOKUS_DANACH_ID = "menu-btn";

export interface OffenAnzeige {
  zeichne(): void;
  schliesse(): void;
}

export interface OffenAktionen {
  /** „Antworten“ getippt: zur Blase, „An:“ auf den Absender, Antwortfeld offen. Öffnet NIE das Mikro. */
  waehle(f: OffeneFrage): void;
}

/** Verdrahtet Marke und Tafel. Ohne die Elemente (andere Seite, Tests ohne Kopf) zeichnet sie nichts. */
export function verdrahteOffeneFragen(store: OffeneFragen, aktionen: OffenAktionen, doc: Document = document): OffenAnzeige {
  const marke = (): HTMLButtonElement | null => {
    const el = doc.getElementById(MARKE_ID);
    return el instanceof HTMLButtonElement ? el : null;
  };
  const tafel = (): HTMLElement | null => doc.getElementById(LISTE_ID);
  const offen = (): boolean => marke()?.getAttribute("aria-expanded") === "true";

  const schliesse = (): void => {
    const t = tafel();
    if (t) t.hidden = true;
    marke()?.setAttribute("aria-expanded", "false");
  };
  const oeffne = (): void => {
    const m = marke();
    const t = tafel();
    if (!m || !t || m.hidden) return;
    t.hidden = false;
    m.setAttribute("aria-expanded", "true");
    // Die Tafel der Entwicklung liegt an derselben Stelle — sie schließt (lib/schmied-arbeit.ts hört darauf).
    meldeTafelAuf(doc, LISTE_ID);
  };

  const knopf = (klasse: string, rolle: string, text: string, name: string, tun: () => void): HTMLButtonElement => {
    const b = doc.createElement("button");
    b.type = "button";
    b.className = klasse;
    b.dataset.rolle = rolle;
    b.textContent = text;
    b.setAttribute("aria-label", name);
    b.addEventListener("click", tun);
    return b;
  };

  /**
   * Fokus auf den Eintrag an Stelle `i` (gibt es ihn nicht mehr, der letzte) — also nach „Erledigt“ auf den NÄCHSTEN; ist keiner mehr da
   * (Marke weg), auf den Menü-Knopf. Nie ins Leere.
   */
  const fokusAufStelle = (i: number): void => {
    const ziele = tafel()?.querySelectorAll<HTMLButtonElement>(".topbar-offen-ziel") ?? [];
    const m = marke();
    const ziel = ziele.length > 0 && m && !m.hidden ? ziele[Math.min(Math.max(0, i), ziele.length - 1)] : null;
    if (ziel) ziel.focus();
    else doc.getElementById(FOKUS_DANACH_ID)?.focus();
  };

  const zeichne = (): void => {
    const m = marke();
    const zahl = doc.getElementById(ZAHL_ID);
    const t = tafel();
    const ul = t?.querySelector("ul");
    if (!m || !zahl || !t || !ul) return;
    const liste = store.liste();
    if (liste.length === 0) {
      // Leer: Marke unsichtbar. Stand der Fokus in der Tafel, geht er zurück ins Dokument (nicht in ein verborgenes Element).
      const hatteFokus = t.contains(doc.activeElement);
      m.hidden = true;
      zahl.textContent = "";
      ul.replaceChildren();
      schliesse();
      if (hatteFokus) doc.getElementById(FOKUS_DANACH_ID)?.focus();
      return;
    }
    m.hidden = false;
    zahl.textContent = String(liste.length);
    m.setAttribute("aria-label", offenSatz(liste.length));
    // Steht der Fokus in der Tafel, merkt sich das Neuzeichnen, WO (Eintrag und Knopf) — danach steht er wieder dort (Auffrischen jede
    // Minute, neue oder beantwortete Frage). Ist sein Eintrag weg, geht er auf den Eintrag, der an dessen Stelle rückte.
    const aktiv = doc.activeElement instanceof HTMLElement && t.contains(doc.activeElement) ? doc.activeElement : null;
    const aktivZeile = aktiv?.closest<HTMLElement>(".topbar-offen-zeile") ?? null;
    const fokus = aktivZeile
      ? {
          id: aktivZeile.dataset.id ?? "",
          rolle: aktiv?.dataset.rolle ?? "ziel",
          stelle: [...ul.children].indexOf(aktivZeile),
        }
      : null;
    const zeilen = liste.map((f, stelle) => {
      const li = doc.createElement("li");
      li.className = "topbar-offen-zeile";
      li.dataset.id = f.id;
      const wer = absenderName(f.von);
      // 22.10.2026 (Martin: „ich kann nur auf ‚Erledigt‘ klicken, aber nichts anstoßen“): der Text ist kein unsichtbarer Knopf
      // mehr; die Zeile trägt EINE sichtbare Hauptaktion „Antworten“ (zum Beitrag, „An:“ auf den Absender, Antwortfeld — kein Mikro).
      const inhalt = doc.createElement("div");
      inhalt.className = "topbar-offen-inhalt";
      const kopf = doc.createElement("span");
      kopf.className = "topbar-offen-kopf";
      const name = doc.createElement("span");
      name.className = "topbar-offen-wer";
      name.textContent = wer;
      const zeit = doc.createElement("span");
      zeit.className = "topbar-offen-zeit";
      zeit.textContent = uhrzeit(f.ts);
      kopf.append(name, zeit);
      const was = doc.createElement("span");
      was.className = "topbar-offen-was";
      was.textContent = f.kurz;
      inhalt.append(kopf, was);
      const ziel = knopf("btn btn--primary topbar-offen-ziel", "ziel", "Antworten", `Antworten an ${wer}: ${f.kurz}`, () => {
        schliesse();
        try {
          aktionen.waehle(f);
        } catch {
          /* die Anzeige darf nie werfen */
        }
      });
      const knoepfe = doc.createElement("span");
      knoepfe.className = "topbar-offen-knoepfe";
      knoepfe.append(
        ziel,
        knopf("topbar-offen-knopf", "erledigt", "Erledigt", `Erledigt: Frage von ${wer} weglegen`, () => {
          store.erledigt(f.id);
          fokusAufStelle(stelle);
        }),
        knopf("topbar-offen-knopf topbar-offen-knopf--leise", "spaeter", "Später", `Später: Frage von ${wer} eine Stunde ausblenden`, () => {
          store.spaeter(f.id);
          fokusAufStelle(stelle);
        }),
      );
      li.append(inhalt, knoepfe);
      return li;
    });
    ul.replaceChildren(...zeilen);
    if (!fokus) return;
    const gleich = zeilen.find((z) => z.dataset.id === fokus.id);
    const knopfDort = gleich?.querySelector<HTMLElement>(`[data-rolle="${fokus.rolle}"]`);
    if (knopfDort) knopfDort.focus();
    else fokusAufStelle(fokus.stelle);
  };

  const m = marke();
  const t = tafel();
  if (m && t) {
    m.addEventListener("click", () => (offen() ? schliesse() : oeffne()));
    doc.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || !offen()) return;
      const warDrin = tafel()?.contains(doc.activeElement) === true;
      schliesse();
      if (warDrin) marke()?.focus();
    });
    doc.addEventListener("pointerdown", (e) => {
      if (!offen()) return;
      const ziel = e.target;
      if (ziel instanceof Node && (marke()?.contains(ziel) || tafel()?.contains(ziel))) return;
      schliesse();
    });
    // Geht eine andere Tafel an derselben Stelle auf (Entwicklung — auch per Tastatur oder Maus), schließt diese.
    doc.addEventListener(TAFEL_AUF, (e) => {
      if ((e as CustomEvent<string>).detail !== LISTE_ID && offen()) schliesse();
    });
    // Verfall und „Später“ laufen ab, auch ohne neue Frage.
    setInterval(zeichne, AUFFRISCHEN_MS);
  }
  zeichne();
  return { zeichne, schliesse };
}
